import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateUserInput,
  ToggleActiveInput,
  UpdateUserInput,
  User,
  UsersListResponse,
} from '@/features/users/types/user.types';
import { apiFetch } from '@/lib/api';

/**
 * Users — TanStack Query hooks.
 *
 * Mirror of `apps/web/src/features/catalog/api/products.ts` pattern but
 * with two end-points worth noting:
 *
 * 1. `useUsers({ role?: 'SELLER' })` — the users route (PR#4 +
 *    PR#6 extension) accepts a `role` query param that filters
 *    memberships by role IN THE CURRENT TENANT. The seller picker in
 *    ClientForm uses `useUsers({ role: 'SELLER', pageSize: 100 })` to
 *    populate the dropdown without over-fetching.
 *
 * 2. `useToggleUserActive()` — the soft-delete primitive. There is no
 *    DELETE /users/:id; the API exposes `PATCH /users/:id/toggle-active`.
 *    The last-admin protection runs at the membership layer (PATCH/DELETE
 *    on /memberships/:id) — toggling users.isActive does NOT remove the
 *    membership, so the last-admin invariant is preserved when only the
 *    active flag changes. The form's UI warning text explains this.
 */

export type UsersSortKey = 'email' | 'createdAt';
export type SortDirection = 'asc' | 'desc';

export interface UsersListParams {
  page: number;
  pageSize: number;
  sort?: UsersSortKey;
  dir?: SortDirection;
  search?: string;
  isActive?: 'active' | 'inactive' | 'all';
  role?: 'ADMIN' | 'SELLER' | 'WAREHOUSE';
  // sdd/finanzas-modulo PR#1 — exclude memberships whose role matches
  // this value (BE applies `memberships.role != excludeRole`). The
  // SellerSelect uses `excludeRole='WAREHOUSE'` to surface SELLER +
  // ADMIN candidates without two parallel filtered queries.
  excludeRole?: 'ADMIN' | 'SELLER' | 'WAREHOUSE';
}

function buildQuery(params: UsersListParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page));
  sp.set('pageSize', String(params.pageSize));
  if (params.sort) sp.set('sort', params.sort);
  if (params.dir) sp.set('dir', params.dir);
  if (params.search) sp.set('search', params.search);
  if (params.role) sp.set('role', params.role);
  if (params.excludeRole) sp.set('excludeRole', params.excludeRole);
  if (params.isActive === 'inactive') sp.set('isActive', 'false');
  if (params.isActive === 'all') sp.set('includeInactive', 'true');
  return sp.toString();
}

export const usersKeys = {
  all: ['users'] as const,
  list: (params: UsersListParams) => ['users', 'list', params] as const,
  detail: (id: string) => ['users', 'detail', id] as const,
};

export function useUsers(params: UsersListParams) {
  return useQuery({
    queryKey: usersKeys.list(params),
    queryFn: () => apiFetch<UsersListResponse>(`/users?${buildQuery(params)}`),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useUser(id: string | null) {
  return useQuery({
    queryKey: usersKeys.detail(id ?? ''),
    queryFn: () => apiFetch<User & { role: UsersListParams['role'] }>(`/users/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) =>
      apiFetch<User>('/users', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      apiFetch<User>(`/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: usersKeys.all });
      qc.invalidateQueries({ queryKey: usersKeys.detail(id) });
    },
  });
}

/**
 * Toggle the `users.isActive` flag — soft-delete / reactivate.
 *
 * Independent of the membership-level last-admin protection: flipping
 * `isActive` to false does NOT remove the membership row, so the tenant
 * retains an ADMIN in the memberships table (just one that cannot log
 * in). The membership-level protections on /memberships/:id still apply
 * if the admin demotes themselves via that endpoint.
 */
export function useToggleUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ToggleActiveInput }) =>
      apiFetch<User>(`/users/${id}/toggle-active`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: usersKeys.all });
      qc.invalidateQueries({ queryKey: usersKeys.detail(id) });
    },
  });
}
